// shared-lib/group-customization/snapshot.js
//
// Persist + replay the relative pose of every child in a fixture group.
// snapshotGroupCustomization → write side (editor save). applyCustomization-
// ToAnchor → read side (fix-all replays the snapshot at the new anchor
// pose). pairCopyMirrorChildren is the shared helper both the solver and
// the editor's copy↔mirror toggle use to walk a copy/mirror link's paired
// children.

const { worldToLocal, localToWorld, computeRpOffset } = require('./transforms');

// Drift epsilon for the invariant check on RP-link offsets. 1 mm in
// anchor-local coords; well above floating-point noise but tight enough
// to flag the bug class deficiency #11 calls out (snapshot encoding a
// stale offset because the working state diverged from the link state).
const RP_OFFSET_DRIFT_EPS_M = 1e-3;

function checkRpOffsetInvariants(snapshot, children) {
  if (
    typeof process !== 'undefined' &&
    process.env?.NODE_ENV === 'production'
  ) {
    return;
  }
  if (!snapshot?.children?.length || !snapshot.links?.length) return;
  const byId = new Map(snapshot.children.map((c) => [c.id, c]));
  for (const link of snapshot.links) {
    if (link?.type !== 'rp') continue;
    const source = byId.get(link.sourceId);
    const target = byId.get(link.targetId);
    if (!source || !target) continue;
    const expected = computeRpOffset(source, target, link.rotationMode);
    const stored = link.offset || { dx: 0, dy: 0, dz: 0 };
    const dx = Math.abs((expected.dx || 0) - (stored.dx || 0));
    const dy = Math.abs((expected.dy || 0) - (stored.dy || 0));
    const dz = Math.abs((expected.dz || 0) - (stored.dz || 0));
    if (
      dx > RP_OFFSET_DRIFT_EPS_M ||
      dy > RP_OFFSET_DRIFT_EPS_M ||
      dz > RP_OFFSET_DRIFT_EPS_M
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `[snapshotGroupCustomization] rp-link ${link.id} offset diverges from working state ` +
          `(Δ=${dx.toFixed(4)},${dy.toFixed(4)},${dz.toFixed(4)} m). ` +
          `Snapshot is encoding the stale stored offset.`
      );
    }
  }
}

/**
 * Snapshot the relative pose of every child in the group.
 *
 * @param {Object} anchor
 * @param {Object[]} children
 * @param {Object[]} [links] — link constraints between children, see
 *   LINK_TYPES (createLink.js).
 * @returns {Object} groupCustomization payload (null if no anchor)
 */
function snapshotGroupCustomization(anchor, children, links) {
  if (!anchor) return null;
  const anchorRotY = anchor.rotation?.y || 0;
  const snapshot = {
    editedAt: new Date().toISOString(),
    anchorId: anchor.id,
    anchorType: anchor.type,
    children: (children || [])
      .filter((c) => c?.id && c?.center)
      .map((child) => {
        const local = worldToLocal(child.center, anchor);
        const childRotY = child.rotation?.y || 0;
        return {
          id: child.id,
          type: child.type || null,
          dx: local.dx,
          dy: local.dy,
          dz: local.dz,
          rotYRel: childRotY - anchorRotY,
          dimensions: child.dimensions
            ? {
                x: child.dimensions.x,
                y: child.dimensions.y,
                z: child.dimensions.z,
              }
            : null,
          parentWallIds: child.parentWallIds || null,
        };
      }),
    links: Array.isArray(links) ? links.map((l) => ({ ...l })) : [],
  };
  checkRpOffsetInvariants(snapshot, children);
  return snapshot;
}

/**
 * Apply a customization snapshot to a new anchor pose, returning child
 * objects with updated centers and rotations. Caller matches these back to
 * existing child objects (by id) and writes them to the room.
 *
 * @param {Object} newAnchor — anchor with new center/rotation
 * @param {Object} customization — output of snapshotGroupCustomization
 * @returns {Array<{id, type, center, rotation, dimensions, parentWallIds}>}
 */
function applyCustomizationToAnchor(newAnchor, customization) {
  if (!newAnchor || !customization?.children) return [];
  const anchorRotY = newAnchor.rotation?.y || 0;
  return customization.children.map((c) => {
    const center = localToWorld({ dx: c.dx, dy: c.dy, dz: c.dz }, newAnchor);
    return {
      id: c.id,
      type: c.type,
      center,
      rotation: { x: 0, y: anchorRotY + (c.rotYRel || 0), z: 0 },
      dimensions: c.dimensions,
      parentWallIds: c.parentWallIds,
    };
  });
}

/**
 * Pair a copy/mirror link's two parents' RP-children index-by-index,
 * grouped by child type and ordered by creation (the order RP links appear
 * in `links`). The 1st A-side faucet pairs with the 1st B-side faucet,
 * etc. — matches the long-standing copy/mirror semantics.
 *
 * Used by:
 *   - constraintSolver.expandCopyMirror to synthesize one `linked-pair`
 *     per matching pair.
 *   - GroupIsolationView.setCopyMirrorType to sign-flip each B-side RP's
 *     stored offset when the user toggles copy ↔ mirror, so re-solve sees
 *     consistent offsets.
 *
 * @param {Object} copyMirrorLink — the copy or mirror link
 * @param {Object[]} links        — full link list (RPs included)
 * @param {Map<string,string>} childTypeById — child id → type
 * @returns {{aRp, bRp, sign, type, index}[]}
 */
function pairCopyMirrorChildren(copyMirrorLink, links, childTypeById) {
  if (
    !copyMirrorLink ||
    (copyMirrorLink.type !== 'copy' && copyMirrorLink.type !== 'mirror')
  ) {
    return [];
  }
  const sign = copyMirrorLink.type === 'mirror' ? -1 : 1;
  const groupBy = (parentId) => {
    const out = new Map();
    for (const l of links || []) {
      if (l.type !== 'rp') continue;
      if (l.sourceId !== parentId) continue;
      const t = childTypeById?.get?.(l.targetId);
      if (!t) continue;
      if (!out.has(t)) out.set(t, []);
      out.get(t).push(l);
    }
    return out;
  };
  const aByType = groupBy(copyMirrorLink.parentAId);
  const bByType = groupBy(copyMirrorLink.parentBId);
  const out = [];
  for (const [type, aRps] of aByType) {
    const bRps = bByType.get(type) || [];
    const pairCount = Math.min(aRps.length, bRps.length);
    for (let i = 0; i < pairCount; i++) {
      out.push({ aRp: aRps[i], bRp: bRps[i], sign, type, index: i });
    }
  }
  return out;
}

module.exports = {
  snapshotGroupCustomization,
  applyCustomizationToAnchor,
  pairCopyMirrorChildren,
};
